import React, { useEffect, useRef } from "react";
import clsx from "clsx";
import { WrapInput } from "./WrapInput";
import { extractAddressComponents, AddressComponents } from "@/utils/address";

interface Props
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  ipSize?: "lg" | "md";
  label?: string;
  isAsterisk?: boolean;
  isError?: boolean;
  errorMessage?: string;
  wrapClassName?: string;
  className?: string;
  iconType?: "stroke" | "fill";
  iconLeft?: React.ElementType;
  iconRight?: React.ElementType;
  iconClassName?: string;
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (components: AddressComponents) => void;
  countryRestriction?: string; // ISO country code like "au" for Australia
}

const GooglePlacesInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      ipSize = "lg",
      label,
      isAsterisk,
      isError,
      errorMessage,
      className,
      wrapClassName,
      iconType = "fill",
      iconLeft: IconLeft = undefined,
      iconRight: IconRight = undefined,
      iconClassName,
      disabled,
      id,
      value,
      onChange,
      onPlaceSelect,
      countryRestriction = "au",
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(
      null
    );
    const onChangeRef = useRef(onChange);
    const onPlaceSelectRef = useRef(onPlaceSelect);

    // Keep refs up to date without triggering effect re-runs
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      onPlaceSelectRef.current = onPlaceSelect;
    }, [onPlaceSelect]);

    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Initialize Google Places Autocomplete
    useEffect(() => {
      if (!inputRef.current) return;

      // Check if Google Maps API is loaded
      if (typeof google === "undefined" || !google.maps?.places) {
        console.warn("Google Maps Places API is not loaded");
        return;
      }

      // Create autocomplete instance
      autocompleteRef.current = new google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: countryRestriction },
          fields: ["address_components", "formatted_address"],
        }
      );

      // Listen for place selection
      const listener = autocompleteRef.current.addListener(
        "place_changed",
        () => {
          const place = autocompleteRef.current?.getPlace();

          if (place && place.address_components) {
            const components = extractAddressComponents(
              place.address_components
            );

            // Update the input value using the ref
            if (onChangeRef.current) {
              onChangeRef.current(components.addressLine1 || "");
            }

            // Call the callback with all address components using the ref
            if (onPlaceSelectRef.current) {
              onPlaceSelectRef.current(components);
            }
          }
        }
      );

      // Cleanup
      return () => {
        if (listener) {
          google.maps.event.removeListener(listener);
        }
      };
    }, [countryRestriction]);

    return (
      <WrapInput
        label={label}
        isAsterisk={isAsterisk}
        errorMessage={errorMessage}
        wrapClassName={wrapClassName}
        iconType={iconType}
        iconLeft={IconLeft}
        iconRight={IconRight}
        id={id}
        iconClassName={iconClassName}
      >
        <input
          {...rest}
          ref={inputRef}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={clsx(
            "block w-full border text-base rounded ",
            ipSize === "md" ? "py-1 pl-2" : "py-[0.375rem] pl-3",
            isError
              ? "border-red focus:outline-none focus:ring-red focus-visible:outline-none focus:border-red"
              : "border-neutral-20 focus:ring-blue focus:border-blue focus-visible:outline-none",
            IconRight ? "pr-10" : "pr-3",
            IconLeft ? "pl-10" : "pl-3",
            disabled ? "bg-neutral-20 text-neutral-40" : "text-neutral",
            className
          )}
          disabled={disabled}
        />
      </WrapInput>
    );
  }
);

GooglePlacesInput.displayName = "GooglePlacesInput";

export default GooglePlacesInput;
